<template>
  <div class="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
    <div class="w-full max-w-xl space-y-10">
      <div>
        <img class="mx-auto h-30 w-auto" :src="logoUrl" alt="ManageRunners logo" />
        <h2
          class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white"
        >
          {{ heading }}
        </h2>
      </div>

      <div
        class="rounded-2xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
        data-testid="mcp-handoff"
        :data-state="state"
      >
        <div
          v-if="state === 'loading'"
          class="text-sm text-gray-500 dark:text-gray-400"
          role="status"
        >
          Loading…
        </div>

        <div v-else-if="state === 'failed'" role="alert" class="flex gap-3">
          <ExclamationTriangleIcon class="size-6 shrink-0 text-amber-500" aria-hidden="true" />
          <div class="text-sm">
            <p class="font-semibold text-gray-900 dark:text-white">{{ failure.title }}</p>
            <p class="mt-1 text-gray-600 dark:text-gray-300" data-testid="handoff-error">
              {{ failure.message }}
            </p>
            <button
              v-if="failure.switchAccount"
              type="button"
              class="mt-4 font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
              @click="switchAccount"
            >
              Switch account
            </button>
            <RouterLink
              v-else
              :to="{ name: 'Dashboard' }"
              class="mt-4 inline-block font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >Go to dashboard</RouterLink
            >
          </div>
        </div>

        <div v-else-if="state === 'done' && handoff?.result" data-testid="handoff-result">
          <div v-if="handoff.result.outcome === 'applied'" class="flex gap-3" role="status">
            <CheckCircleIcon class="size-6 shrink-0 text-green-500" aria-hidden="true" />
            <div class="text-sm text-gray-600 dark:text-gray-300">
              <p class="font-semibold text-gray-900 dark:text-white">{{ doneTitle }}</p>
              <p v-if="handoff.result.recreatedRunners?.length" class="mt-1">
                The servers of
                {{ handoff.result.recreatedRunners.map((runner) => runner.name).join(', ') }} are
                being recreated.
              </p>
              <p class="mt-2">
                Return to {{ handoff.clientName ?? 'the app' }} and tell it you are done, so it can
                check the result.
              </p>
            </div>
          </div>
          <div v-else class="flex gap-3" role="alert">
            <ExclamationTriangleIcon class="size-6 shrink-0 text-amber-500" aria-hidden="true" />
            <div class="text-sm text-gray-600 dark:text-gray-300">
              <p class="font-semibold text-gray-900 dark:text-white">
                The result could not be confirmed
              </p>
              <p class="mt-1">
                The change may or may not have been made. Check the dashboard before you try again.
              </p>
              <p v-if="handoff.result.requestId" class="mt-1 font-mono text-xs break-all">
                Request ID {{ handoff.result.requestId }}
              </p>
            </div>
          </div>
          <RouterLink
            :to="{ name: resultRoute }"
            class="mt-4 inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
            >Open the dashboard</RouterLink
          >
        </div>

        <form v-else-if="handoff && draft" autocomplete="off" @submit.prevent="submit">
          <div class="text-sm text-gray-600 dark:text-gray-300">
            <p>
              <span class="font-semibold text-gray-900 dark:text-white" data-testid="client-name">{{
                handoff.clientName ?? 'An app'
              }}</span>
              prepared this in
              <span class="font-semibold text-gray-900 dark:text-white">{{
                handoff.organizationName ?? 'your organization'
              }}</span
              >. Check it and enter
              {{ secretFields.length === 1 ? 'the secret' : 'the secrets' }} below. They go straight
              to ManageRunners; the app never sees them.
            </p>
            <p class="mt-2">
              Signed in as
              <span class="font-medium text-gray-900 dark:text-white">{{ auth.username }}</span
              >.
              <button
                type="button"
                class="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
                @click="switchAccount"
              >
                Switch account
              </button>
            </p>
          </div>

          <dl
            class="mt-6 divide-y divide-gray-100 border-y border-gray-100 text-sm dark:divide-white/10 dark:border-white/10"
            data-testid="handoff-settings"
          >
            <div v-for="row in rows" :key="row.label" class="grid grid-cols-3 gap-x-4 gap-y-1 py-2">
              <!-- Rules take the full width on phones, so each part fits on a line. -->
              <dt
                class="font-medium text-gray-900 dark:text-white"
                :class="{ 'col-span-3 sm:col-span-1': row.lines }"
              >
                {{ row.label }}
              </dt>
              <dd
                v-if="row.lines"
                class="col-span-3 text-gray-700 sm:col-span-2 dark:text-gray-300"
              >
                <ul>
                  <li v-for="parts in row.lines" :key="parts.join(' ')">
                    {{ parts.join(' ') }}
                  </li>
                </ul>
              </dd>
              <dd v-else class="col-span-2 text-gray-700 break-all dark:text-gray-300">
                {{ row.value }}
              </dd>
            </div>
          </dl>

          <p
            v-if="draft.kind === 'runner' && provider === 'digitalocean'"
            class="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
            data-testid="handoff-price"
          >
            Creating the runner starts a DigitalOcean droplet that costs
            <span class="font-semibold">{{ price || 'the current list price' }}</span> per month,
            billed per second by DigitalOcean to the account of your API token until you pause or
            delete it. The runner keeps a reserved IPv4 and IPv6 address; while it is paused, the
            reserved IPv4 costs USD 5 per month.
          </p>
          <p
            v-else-if="draft.kind === 'runner'"
            class="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
            data-testid="handoff-price"
          >
            Creating the runner starts a Hetzner server that costs
            <span class="font-semibold">{{ price || 'the current list price' }}</span> per month,
            billed by Hetzner to the project of your API token until you pause or delete it. A
            paused runner keeps its IPv4 address, which Hetzner still bills.
          </p>

          <div
            v-if="affected.length"
            class="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
            data-testid="affected-runners"
          >
            <p>Saving this affects the runners that use it:</p>
            <ul class="mt-1 list-disc pl-5">
              <li v-for="runner in affected" :key="runner.runnerId">
                {{ runner.name }}
                {{
                  runner.recreate
                    ? '(its server is recreated, which interrupts running jobs)'
                    : '(keeps the previous settings until it is recreated)'
                }}
              </li>
            </ul>
            <label class="mt-2 flex gap-2">
              <input
                v-model="confirmRecreate"
                type="checkbox"
                class="mt-0.5 size-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600"
                data-testid="confirm-recreate"
              />
              <span>I understand; save the change for these runners.</span>
            </label>
          </div>

          <p
            v-if="draft?.affectedExternalRunners?.length"
            class="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
            data-testid="affected-external-runners"
          >
            The external runners
            {{ draft.affectedExternalRunners.map((runner) => runner.name).join(', ') }} keep sending
            with the previous settings until you run a new install command on each server.
          </p>

          <fieldset class="mt-6 space-y-4">
            <legend class="sr-only">Secrets</legend>
            <div v-for="field in secretFields" :key="field.key">
              <label
                :for="`secret-${field.key}`"
                class="block text-sm/6 font-medium text-gray-900 dark:text-white"
                >{{ field.label }}</label
              >
              <input
                :id="`secret-${field.key}`"
                v-model="values[field.key]"
                type="password"
                @blur="field.key === cloudTokenKey && listSshKeys()"
                autocomplete="new-password"
                spellcheck="false"
                required
                class="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10"
              />
              <p v-if="field.hint" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {{ field.hint }}
              </p>
            </div>
          </fieldset>

          <div v-if="draft.kind === 'cache'" class="mt-3 text-sm">
            <button
              type="button"
              :disabled="busy || testing || !values.secretKey"
              class="font-semibold text-indigo-600 hover:text-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-indigo-400 cursor-pointer"
              @click="test"
            >
              {{ testing ? 'Testing…' : 'Test connection' }}
            </button>
            <p
              v-if="testResult"
              class="mt-1"
              :class="
                testResult.success
                  ? 'text-green-700 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              "
              data-testid="cache-test-result"
            >
              {{ testResult.message }}
            </p>
          </div>

          <p v-if="actionError" class="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {{ actionError }}
          </p>
          <p
            v-if="secretsCleared"
            class="mt-2 text-sm text-gray-600 dark:text-gray-300"
            data-testid="secrets-cleared"
          >
            The secrets you entered were cleared from this page. Enter them again to retry.
          </p>

          <p class="mt-4 text-xs text-gray-500 dark:text-gray-400">
            This link works once and expires at {{ expiresAt }}.
          </p>

          <div class="mt-6 flex justify-end">
            <button
              type="submit"
              :disabled="!canSubmit"
              class="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
            >
              {{ state === 'submitting' ? 'Saving…' : submitLabel }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import {
  getHandoff,
  handoffProblem,
  submitHandoff,
  testHandoffCache,
  type AffectedRunner,
  type McpHandoff,
} from '@/api/mcp-handoffs';
import type { CacheTestResult } from '@/api/caches';
import type { SshKeyDto } from '@/api/hetzner';
import { listProducts, type Product } from '@/api/products';
import { formatMonthlyNetPrice } from '@/lib/format';
import {
  cloudTokenSecret,
  handoffRows,
  handoffSecretFields,
  handoffSubmission,
} from '@/lib/mcp-handoffs';
import { cloudProvider, listProviderSshKeys } from '@/lib/providers';
import { getHandoffSecret, handoffLinkRevision } from '@/lib/mcp-handoff-link';
import { useAuthStore } from '@/stores/auth';

type State = 'loading' | 'ready' | 'submitting' | 'done' | 'failed';

const logoUrl = new URL('../assets/logo_runner_small.png', import.meta.url).href;
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

const draftId = ref(getHandoffSecret() ?? '');

const state = ref<State>('loading');
const handoff = ref<McpHandoff | null>(null);
const values = reactive<Record<string, string>>({});
const confirmRecreate = ref(false);
const affectedOverride = ref<AffectedRunner[] | null>(null);
const actionError = ref<string | null>(null);
const secretsCleared = ref(false);
const failure = ref({ title: '', message: '', switchAccount: false });
const testing = ref(false);
const testResult = ref<CacheTestResult | null>(null);

const draft = computed(() => handoff.value?.draft ?? null);
const busy = computed(() => state.value === 'submitting');
const affected = computed(() => affectedOverride.value ?? draft.value?.affectedRunners ?? []);
const secretFields = computed(() => (draft.value ? handoffSecretFields(draft.value) : []));
const products = ref<Product[]>([]);
const sshKeys = ref<SshKeyDto[]>([]);
const rows = computed(() =>
  draft.value ? handoffRows(draft.value, { products: products.value, sshKeys: sshKeys.value }) : []
);
const price = computed(() => formatMonthlyNetPrice(draft.value?.monthlyPrice));
const provider = computed(() => cloudProvider(draft.value?.provider));
const cloudTokenKey = computed(() => (draft.value ? cloudTokenSecret(draft.value) : undefined));
const expiresAt = computed(() =>
  handoff.value ? new Date(handoff.value.expiresAt).toLocaleTimeString() : ''
);
const canSubmit = computed(
  () =>
    state.value === 'ready' &&
    secretFields.value.every((field) => (values[field.key] ?? '').trim() !== '') &&
    (affected.value.length === 0 || confirmRecreate.value)
);

const NOUNS = {
  runner: 'runner',
  cache: 'cache',
  metrics_connection: 'metrics connection',
  scaling_policy: 'scaling policy',
} as const;

const heading = computed(() => {
  const kind = handoff.value?.kind;
  if (!kind) return 'Finish a change';
  if (kind === 'scaling_policy')
    return `Set up autoscaling of ${draft.value?.name ?? 'an external runner'}`;
  const noun = NOUNS[kind];
  return draft.value?.action === 'update' ? `Update ${noun}` : `Create a ${noun}`;
});
const submitLabel = computed(() => (draft.value?.kind === 'runner' ? 'Create runner' : 'Save'));
const doneTitle = computed(() => {
  const kind = handoff.value?.kind;
  if (kind === 'runner') {
    const name = handoff.value?.result?.name;
    return name ? `Runner ${name} is being created.` : 'The runner is being created.';
  }
  if (kind === 'scaling_policy') return 'The scaling policy was saved.';
  return kind === 'cache' ? 'The cache was saved.' : 'The metrics connection was saved.';
});
const resultRoute = computed(() => {
  const kind = handoff.value?.kind;
  if (kind === 'cache') return 'Caches';
  if (kind === 'metrics_connection') return 'Metrics';
  return 'Dashboard';
});

function fail(title: string, message: string, switchAccount = false) {
  failure.value = { title, message, switchAccount };
  state.value = 'failed';
}

/** Ends the page for problems a retry on this link cannot fix; returns false for the others. */
function failOn(error: unknown): boolean {
  const problem = handoffProblem(error);
  if (!problem) return false;
  switch (problem.code) {
    case 'HANDOFF_NOT_FOUND':
      fail('This link is invalid or has expired', 'Ask the app to prepare the change again.');
      return true;
    case 'HANDOFF_EXPIRED':
      fail(
        'This link has expired',
        'Links work for 30 minutes. Ask the app to prepare the change again.'
      );
      return true;
    case 'HANDOFF_REFERENCE_MISSING':
      fail(
        'Something this runner uses is gone',
        'The server type, cache or metrics connection it uses no longer exists. Ask the app to prepare the runner again.'
      );
      return true;
    case 'HANDOFF_STALE':
      fail(
        'This change is out of date',
        'The setting changed after the app prepared this change. Ask the app to prepare it again.'
      );
      return true;
    case 'HANDOFF_WRONG_ACCOUNT':
      fail(
        'This link belongs to another account',
        'Sign in with the account that connected the app.',
        true
      );
      return true;
    case 'HANDOFF_CONNECTION_REVOKED':
      fail(
        'The app was disconnected',
        'The connection that prepared this change was disconnected or lost its permission to change runners.'
      );
      return true;
    case 'ORGANIZATION_NOT_FOUND':
      fail('Organization not available', 'You are no longer a member of this organization.');
      return true;
    case 'ORGANIZATION_PERMISSION_DENIED':
      fail(
        'Not allowed',
        'Your role in this organization does not allow this change. Ask an owner to change your role.'
      );
      return true;
    default:
      return false;
  }
}

// The handoff's organization need not be the selected one, so these lookups go without it.
async function loadProducts() {
  try {
    products.value = await listProducts({ withoutOrganization: true });
  } catch {
    products.value = [];
  }
}

function cloudToken() {
  return cloudTokenKey.value ? (values[cloudTokenKey.value] ?? '').trim() : '';
}

async function listSshKeys() {
  const token = cloudToken();
  if (draft.value?.kind !== 'scaling_policy' || !draft.value.sshKeyIds?.length || !token) return;
  try {
    const keys = await listProviderSshKeys(provider.value, token, { withoutOrganization: true });
    if (cloudToken() === token) sshKeys.value = keys;
  } catch {
    // Submitting reports a refused token.
  }
}

watch(cloudToken, () => (sshKeys.value = []));

function current(revision: number) {
  return revision === handoffLinkRevision.value;
}

async function load() {
  const revision = handoffLinkRevision.value;
  try {
    const loaded = await getHandoff(draftId.value);
    if (!current(revision)) return;
    handoff.value = loaded;
    affectedOverride.value = null;
    state.value = loaded.state === 'done' ? 'done' : 'ready';
    if (loaded.draft?.kind === 'scaling_policy') void loadProducts();
  } catch (e) {
    if (!current(revision)) return;
    if (!failOn(e)) fail('The link could not be loaded', 'Try again later.');
  }
}

async function open() {
  if (!draftId.value) {
    fail('This link is invalid', 'Open the link from the app again.');
    return;
  }
  await load();
}

onMounted(open);

watch(handoffLinkRevision, async () => {
  draftId.value = getHandoffSecret() ?? '';
  handoff.value = null;
  for (const key of Object.keys(values)) delete values[key];
  confirmRecreate.value = false;
  affectedOverride.value = null;
  actionError.value = null;
  secretsCleared.value = false;
  failure.value = { title: '', message: '', switchAccount: false };
  testing.value = false;
  testResult.value = null;
  state.value = 'loading';
  await open();
});

async function submit() {
  if (!canSubmit.value || !handoff.value || !draft.value) return;
  const revision = handoffLinkRevision.value;
  state.value = 'submitting';
  actionError.value = null;
  const submission = handoffSubmission(draft.value, values, affected.value.length > 0);
  // Cleared before the request, so no outcome, refusal or network error leaves a secret in the inputs.
  for (const key of Object.keys(values)) values[key] = '';
  try {
    const submitted = await submitHandoff(draftId.value, submission);
    if (!current(revision)) return;
    handoff.value = submitted;
    secretsCleared.value = false;
    state.value = 'done';
  } catch (e) {
    if (!current(revision)) return;
    secretsCleared.value = true;
    await refused(e);
  }
}

async function refused(error: unknown) {
  if (failOn(error)) return;
  const problem = handoffProblem(error);
  state.value = 'ready';
  switch (problem?.code) {
    case 'HANDOFF_USED':
      await load();
      return;
    case 'RECREATE_REQUIRED':
      affectedOverride.value = problem.runners ?? [];
      confirmRecreate.value = false;
      actionError.value = 'The runners using this changed. Check the list and confirm again.';
      return;
    case 'PREVIEW_STALE':
      // Before the reload, so that a link opened meanwhile resets them for its own page.
      confirmRecreate.value = false;
      actionError.value = 'Something shown here changed meanwhile. Check it and confirm again.';
      await load();
      return;
    case 'HANDOFF_IN_PROGRESS':
      actionError.value = 'This change is already being saved. Wait a moment and reload the page.';
      return;
    default:
      actionError.value = problem?.detail
        ? problem.detail
        : problem
          ? `The change could not be saved (HTTP ${problem.status ?? 'error'}). Try again later.`
          : 'The server is unreachable. Check your connection and try again.';
  }
}

async function test() {
  if (!values.secretKey) return;
  const revision = handoffLinkRevision.value;
  testing.value = true;
  testResult.value = null;
  actionError.value = null;
  try {
    const result = await testHandoffCache(draftId.value, values.secretKey);
    if (current(revision)) testResult.value = result;
  } catch (e) {
    if (current(revision) && !failOn(e)) {
      const problem = handoffProblem(e);
      actionError.value = problem?.detail ?? 'The connection could not be tested. Try again later.';
    }
  } finally {
    if (current(revision)) testing.value = false;
  }
}

async function switchAccount() {
  auth.logout();
  await router.push({ name: 'Login', query: { redirect: route.fullPath } });
}
</script>
